import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * BulkActionBar — a sticky floating bar that appears at the bottom of
 * the viewport when 1+ items are selected. Renders selection summary +
 * a slot for tab-specific action buttons + a Clear-selection button.
 *
 * Props:
 *   count: number
 *   label: string ("campaign" | "student" | "message" | "disbursement")
 *   onClear: () => void
 *   children: action buttons (already testid'd by the parent tab)
 */
export const BulkActionBar = ({ count, label, onClear, children }) => (
  <AnimatePresence>
    {count > 0 && (
      <motion.div
        role="toolbar"
        aria-label={`Bulk actions on ${count} ${label}${count === 1 ? "" : "s"}`}
        data-testid="bulk-action-bar"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
        className="fixed inset-x-0 bottom-6 z-40 mx-auto flex w-[min(92vw,780px)] items-center justify-between gap-4 rounded-full border-2 border-ink bg-ink px-6 py-4 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.35)]"
      >
        <div className="flex items-center gap-3 text-sand">
          <span
            data-testid="bulk-selection-count"
            className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-yellow px-2.5 text-xs font-bold text-ink"
          >
            {count}
          </span>
          <span className="font-serif text-lg">
            {label}
            {count === 1 ? "" : "s"} selected
          </span>
        </div>
        <div className="flex items-center gap-2">
          {children}
          <button
            type="button"
            onClick={onClear}
            data-testid="bulk-clear-selection"
            aria-label="Clear selection"
            className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full border border-sand/25 text-sand transition-colors hover:bg-sand/10"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);
